import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Audit.css';

const Audit = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterAction, setFilterAction] = useState('todas');
  const [filterTable, setFilterTable] = useState('todas');
  const [searching, setSearching] = useState('');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/audit/logs');
      setLogs(response.data);
      setError('');
    } catch (err) {
      console.error('Error al cargar logs:', err);
      setError('Error al cargar los logs de auditoría');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const actionLower = action.toLowerCase();
    if (actionLower.includes('insert') || actionLower.includes('create')) return 'badge-insert';
    if (actionLower.includes('update')) return 'badge-update';
    if (actionLower.includes('delete')) return 'badge-delete';
    return 'badge-other';
  };

  const filteredLogs = logs.filter(log => {
    const matchesAction = filterAction === 'todas' || log.action.toLowerCase().includes(filterAction.toLowerCase());
    const matchesTable = filterTable === 'todas' || log.table_name.toLowerCase().includes(filterTable.toLowerCase());
    const matchesSearch = !searching || 
      log.user_name?.toLowerCase().includes(searching.toLowerCase()) ||
      log.table_name?.toLowerCase().includes(searching.toLowerCase()) ||
      log.action?.toLowerCase().includes(searching.toLowerCase());
    
    return matchesAction && matchesTable && matchesSearch;
  });

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searching, filterAction, filterTable]);

  // Obtener tablas únicas para el filtro
  const tables = [...new Set(logs.map(log => log.table_name))].sort();

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="🔍 Auditoría del Sistema" />

        <div className="page-content">
          <div className="page-header">
            <div className="header-title">
              <h2>Logs de Actividad</h2>
              <p className="subtitle">Seguimiento de cambios realizados en el sistema</p>
            </div>
            <button onClick={fetchLogs} className="btn-refresh" disabled={loading}>
              {loading ? '⏳' : '🔄 Actualizar'}
            </button>
          </div>

          <div className="filters-bar">
            <div className="filter-group">
              <label>🔍 Buscar:</label>
              <input 
                type="text" 
                placeholder="Usuario, tabla o acción..."
                value={searching}
                onChange={(e) => setSearching(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>⚡ Acción:</label>
              <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
                <option value="todas">Todas</option>
                <option value="INSERT">Creación</option>
                <option value="UPDATE">Actualización</option>
                <option value="DELETE">Eliminación</option>
              </select>
            </div>
            <div className="filter-group">
              <label>📋 Tabla:</label>
              <select value={filterTable} onChange={(e) => setFilterTable(e.target.value)}>
                <option value="todas">Todas</option>
                {tables.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <p className="loading">Cargando logs de auditoría...</p>
          ) : filteredLogs.length === 0 ? (
            <p className="no-data">No se encontraron registros de auditoría</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="audit-table">
                <thead>
                  <tr>
                    <th>Fecha y Hora</th>
                    <th>Usuario</th>
                    <th>Acción</th>
                    <th>Tabla</th>
                    <th>ID Registro</th>
                    <th>Detalles</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(log => (
                    <tr key={log.id}>
                      <td className="audit-date">{formatDate(log.created_at)}</td>
                      <td className="audit-user">{log.user_name || 'Sistema'}</td>
                      <td>
                        <span className={`audit-badge ${getActionBadge(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="audit-table-name">{log.table_name}</td>
                      <td className="audit-id">#{log.record_id}</td>
                      <td className="audit-details">
                        <div className="changes-preview">
                          {log.changes && log.changes !== '{}' ? (
                            <pre>{JSON.stringify(JSON.parse(log.changes), null, 2)}</pre>
                          ) : (
                            <span className="no-changes">Sin detalles</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              
              <Pagination 
                currentPage={currentPage}
                totalItems={filteredLogs.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Audit;
